import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
// import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { BlockUIModule } from 'primeng/blockui';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SignupOnboardingService } from '../services/signup-onboarding/signup-onboarding.service';

import { DaPersonalDetailsComponent } from './sections/personal-details/personal-details.component';
import { DaParentsDetailsComponent } from './sections/parents-details/parents-details.component';
import { DaEducationComponent } from './sections/education/education.component';
import { DaRecommendationsComponent } from './sections/recommendations/recommendations.component';
import { DaExperienceComponent } from './sections/experience/experience.component';
import { DaAwardsComponent } from './sections/awards/awards.component';
import { DaTestScoresComponent } from './sections/test-scores/test-scores.component';
import { ReviewComponent } from './review/review.component';
import { PaymentComponent } from './payment/payment.component';
import { ConfirmationComponent } from './confirmation/confirmation.component';
import { DirectApplyLandingComponent } from './direct-apply-landing/direct-apply-landing.component';
import { ExpertCheckoutComponent } from './expert-checkout/expert-checkout.component';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
@Component({
  selector: 'app-direct-apply',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BlockUIModule,
    ToastModule,
    DaPersonalDetailsComponent,
    DaParentsDetailsComponent,
    DaEducationComponent,
    DaRecommendationsComponent,
    DaExperienceComponent,
    DaAwardsComponent,
    DaTestScoresComponent,
    ReviewComponent,
    PaymentComponent,
    ConfirmationComponent,
    DirectApplyLandingComponent,
    ExpertCheckoutComponent
  ],
  providers: [MessageService],
  templateUrl: './direct-apply.component.html',
  styleUrl: './direct-apply.component.scss'
})
export class DirectApplyComponent implements OnInit {

  currentStep: number = 0;
  maxSteps: number = 4;
  isPageLoading: boolean = false;

  // Payment result passed to the Payment step (3) after returning from the gateway callback.
  paymentTxnId: string = '';
  paymentIsSuccess: boolean | null = null;
  paymentFailureReason: string | null = null;

  mode: 'standard' | 'expert' = 'standard';

  daForm!: FormGroup;

  @ViewChild(ReviewComponent) reviewComponent?: ReviewComponent;
  @ViewChild(ExpertCheckoutComponent) expertCheckoutComponent?: ExpertCheckoutComponent;

  genderOptions = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Other', value: 'Other' }
  ];

  yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  constructor(
    private fb: FormBuilder,
    private signupOnboardingService: SignupOnboardingService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private messageService: MessageService
  ) { }

  sectionsLoaded: number = 0;
  totalSections: number = 6; // Personal Details, Parents Details, Education

ngOnInit() {
  const savedMode = sessionStorage.getItem('da_mode');
  if (savedMode) {
    this.mode = savedMode as 'standard' | 'expert';
  }

  this.initForm();

  const fullUrl = this.router.url;

  if (fullUrl.includes('/payment-review/') || fullUrl.includes('/payment-expert/')) {
    const isSuccess = fullUrl.includes('success=True') || fullUrl.includes('success=true') || fullUrl.includes('success%3DTrue');
    const isFailure = fullUrl.includes('success=False') || fullUrl.includes('success=false') || fullUrl.includes('success%3DFalse');

    if (fullUrl.includes('/payment-expert/')) {
      if (isFailure) {
        setTimeout(() => {
          this.messageService.add({ severity: 'error', summary: 'Payment Failed', detail: 'Payment failed, please try again later.' });
        });
        this.currentStep = -1;
      } else if (isSuccess) {
        this.completeExpertCheckout();
      }
      this.router.navigate(['/portal/direct-apply'], { replaceUrl: true });
      return;
    }

    // Direct Apply payment: land on the Payment step (3) and show the status there.
    this.paymentTxnId = this.extractPaymentTxnId(fullUrl);
    this.paymentFailureReason = this.extractPaymentReason(fullUrl);
    this.paymentIsSuccess = isSuccess ? true : (isFailure ? false : null);
    this.goToStep(3, true);
    // Clean the URL without re-creating the component so the Payment step keeps its inputs.
    this.location.replaceState('/portal/direct-apply');
    return;
  }

  this.route.queryParams.subscribe(params => {
    if (params['step']) {
      const stepParam = parseInt(params['step'], 10);
      if (!isNaN(stepParam)) {
        this.goToStep(stepParam);
      }
    }
  });
}

  startFromLanding(mode: string) {
    this.mode = mode as 'standard' | 'expert';
    sessionStorage.setItem('da_mode', this.mode);
    this.isPageLoading = true;
    this.sectionsLoaded = 0;
    this.totalSections = 6;
    this.currentStep = 1;
  }

  completeExpertCheckout() {
    this.isPageLoading = true;
    this.sectionsLoaded = 0;
    this.totalSections = 6;
    this.currentStep = 1;
  }

  cancelExpertCheckout() {
    this.currentStep = 0;
  }

  onSectionLoaded() {
    this.sectionsLoaded++;
    if (this.sectionsLoaded >= this.totalSections) {
      // Small timeout to let UI settle
      setTimeout(() => {
        this.isPageLoading = false;
      }, 300);
    }
  }

  initForm() {
    this.daForm = this.fb.group({
      // Child components will register their controls/arrays inside these groups dynamically if not already preset
      personalDetails: this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        dob: [null, Validators.required],
        gender: [null, Validators.required],
        currentLocation: ['', Validators.required]
      })
    });
  }

  getUserId(): string {
    try {
      const raw = localStorage.getItem('account');
      return raw ? JSON.parse(raw)?.userId ?? '' : '';
    } catch {
      return '';
    }
  }

goToStep(step: number, bypassGuard: boolean = false) {
  // Sequential guard — payment callback pe bypass karo
  if (!bypassGuard) {
    if (step === 4 && this.currentStep !== 2) return;
    if (step === 3 && this.currentStep !== 2) return;
  }

  if (step >= 1 && step <= this.maxSteps) {
    if (this.currentStep !== step) {
      this.isPageLoading = true;
      this.sectionsLoaded = 0;

      if (step === 1) {
        this.totalSections = 3;
      } else if (step === 2 || step === 4) {
        this.totalSections = 1;
      } else {
        this.totalSections = 0;
        setTimeout(() => { this.isPageLoading = false; });
      }
    }
    this.isNavigating = false;
    this.currentStep = step;
  }
}

  isNavigating: boolean = false;
nextStep() {
  if (this.isNavigating) return;

  if (this.currentStep === 1) {
    // Mark all controls touched to trigger validation UI
    this.daForm.markAllAsTouched();

    // Check for unsaved changes across all sections first
    const sections = [
      'personalDetails', 'parentsDetails'
    ];
    const arraySection = [
      'education', 'recommendations', 'experience', 'awards', 'testScores'
    ];

    const sectionDirty = sections.some(s => this.daForm.get(s)?.dirty);
    const arrayDirty = arraySection.some(s => {
      const arr = this.daForm.get(s) as any;
      return arr?.controls?.some((c: any) => c.dirty);
    });
    const tsDirty = this.daForm.get('testScores') && 
      (this.daForm.get('testScores') as any)?.controls?.some((c: any) => c.dirty);
      
    const hasAwardsDirty = this.daForm.get('hasAwards')?.dirty;
    const hasExperienceDirty = this.daForm.get('hasExperience')?.dirty;
    const hasElpDirty = this.daForm.get('hasElpTest')?.dirty;

    if (sectionDirty || arrayDirty || tsDirty || hasAwardsDirty || hasExperienceDirty || hasElpDirty) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Unsaved Changes',
        detail: 'Please save all sections before proceeding.'
      });
      setTimeout(() => {
        // All .unsaved-warning elements are controlled by *ngIf so any in the DOM are visible
        const unsavedContainer = document.querySelector('.unsaved-warning');
        if (unsavedContainer) {
          unsavedContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    const recs = this.daForm.get('recommendations') as any;
    let disabledRecs: any[] = [];
    if (recs && recs.controls) {
      recs.controls.forEach((ctrl: any) => {
        if (ctrl.pristine && !ctrl.value.id) {
          ctrl.disable({ emitEvent: false });
          disabledRecs.push(ctrl);
        }
      });
    }

    const isInvalid = this.daForm.invalid;
    if (isInvalid) {
        this.logInvalidControls(this.daForm);
    }

    disabledRecs.forEach(ctrl => {
      ctrl.enable({ emitEvent: false });
      ctrl.markAsUntouched();
    });

    if (isInvalid) {
      this.scrollToFirstInvalidControl();
      this.messageService.add({
        severity: 'error',
        summary: 'Incomplete Form',
        detail: 'Please fill in all required fields before proceeding.'
      });
      return;
    }

    this.isNavigating = true;
    this.goToStep(2);
    return;
  }

  if (this.currentStep === 2) {
    if (this.mode === 'standard' && this.reviewComponent) {
      this.reviewComponent.processPayment();
    } else if (this.mode === 'expert' && this.expertCheckoutComponent) {
      this.expertCheckoutComponent.onContinue();
    }
    return;
  }

  // Payment step → confirmation, only once the payment has succeeded.
  if (this.currentStep === 3) {
    if (this.paymentIsSuccess) {
      this.goToStep(4, true);
    }
    return;
  }

  this.goToStep(this.currentStep + 1);
}

/** Extracts the merchant txn id from a /payment-review/{txnId}?... callback URL. */
private extractPaymentTxnId(url: string): string {
  const match = url.match(/\/payment-review\/([^/?]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/** Extracts the human-readable failure reason from the callback query string. */
private extractPaymentReason(url: string): string | null {
  const match = url.match(/[?&]reason=([^&]+)/);
  return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
}

  private scrollToFirstInvalidControl() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid',
        '.ng-invalid .error-msg',
        '.ng-invalid .form-group-error'
      ];
      const firstInvalidControl = document.querySelector(selectors.join(', '));

      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Optional: focus the element if it's focusable
        const focusable = firstInvalidControl as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }

  isNextDisabled(): boolean {
    return false;
  }
private logInvalidControls(formGroup: FormGroup | FormArray, path: string = ''): void {
  Object.keys(formGroup.controls).forEach(key => {
    const control = (formGroup as any).get(key);
    const fullPath = path ? `${path}.${key}` : key;

    if (control instanceof FormGroup || control instanceof FormArray) {
      this.logInvalidControls(control, fullPath);
    } else if (control && control.invalid) {
      console.warn(`❌ INVALID: ${fullPath}`, {
        value: control.value,
        errors: control.errors,
        status: control.status,
        disabled: control.disabled
      });
    }
  });

  // Group/Array-level errors (cross-field validators jaise yearRangeValidator, parentValidator, testScoreValidator)
  if ((formGroup as any).errors) {
    console.warn(`❌ GROUP-LEVEL ERROR at "${path || 'root'}"`, (formGroup as any).errors);
  }
}
  saveStep1Data() {
    const ay = this.daForm.value.personalDetails;
    const pd = this.daForm.value.parentsDetails;
    const userId = this.getUserId();
const stepToGoTo = this.currentStep + 1;
    const profilePayload: any = {
      firstName: ay.firstName?.trim() || '',
      lastName: ay.lastName?.trim() || '',
      bornOn: ay.dob ? new Date(ay.dob).toISOString() : null,
      gender: ay.gender === 'Male' ? 0 : ay.gender === 'Female' ? 1 : 2,
      location: ay.currentLocation || ''
    };

    const calls = [this.signupOnboardingService.updateDemography(profilePayload)];

    if (pd.fatherEmail) {
      calls.push(this.signupOnboardingService.createParentInfo({
        studentId: userId,
        name: pd.fatherName?.trim() || null,
        email: pd.fatherEmail?.trim(),
        phone: pd.fatherMobile?.trim() || null,
        relation: 1 // Father
      }));
    }
    if (pd.motherEmail) {
      calls.push(this.signupOnboardingService.createParentInfo({
        studentId: userId,
        name: pd.motherName?.trim() || null,
        email: pd.motherEmail?.trim(),
        phone: pd.motherMobile?.trim() || null,
        relation: 2 // Mother
      }));
    }

     forkJoin(calls).subscribe({
    next: () => {
      this.isNavigating = false;
      this.goToStep(stepToGoTo);
    },
    error: (err) => {
      console.error('Failed to save:', err);
      this.isNavigating = false;
      this.goToStep(stepToGoTo);
    }
  });
}

  prevStep() {
    this.goToStep(this.currentStep - 1);
  }

  goToDashboard() {
    this.router.navigate(['/portal/dashboard']);
  }
}
